/**
 * Tableaux — les listes du back-office (souscriptions, impayés, propositions…).
 *
 * Deux niveaux d’usage :
 *  - les primitives (`Table`, `Th`, `Td`…) pour un tableau sur mesure ;
 *  - `<DataTable>` pour le cas courant « colonnes + lignes + état vide », qui
 *    évite que chaque module réinvente ses en-têtes et ses alignements.
 *
 * Le tri de colonne fait partie du socle (`ColonneTriable`, `ThTriable`,
 * `basculerTri`) : une seule flèche, un seul libellé lu par les lecteurs
 * d’écran, un seul comportement de bascule pour toutes les listes de l’appli.
 */

import type { ReactNode } from 'react'

import { cn } from '../lib/cn'

export type Alignement = 'gauche' | 'centre' | 'droite'

const ALIGNEMENTS: Readonly<Record<Alignement, string>> = {
  gauche: 'text-left',
  centre: 'text-center',
  droite: 'text-right',
}

/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */

export interface TableProps {
  /** Résumé lu par les lecteurs d’écran, invisible à l’écran. */
  readonly legende?: ReactNode | undefined
  readonly className?: string | undefined
  readonly children: ReactNode
}

export function Table({ legende, className, children }: TableProps) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full border-collapse text-sm', className)}>
        {legende !== undefined && <caption className="sr-only">{legende}</caption>}
        {children}
      </table>
    </div>
  )
}

export function Thead({ children }: { readonly children: ReactNode }) {
  return <thead className="border-b border-slate-200 bg-slate-50">{children}</thead>
}

export function Tbody({ children }: { readonly children: ReactNode }) {
  return <tbody className="divide-y divide-slate-100">{children}</tbody>
}

export interface TrProps {
  readonly onClick?: (() => void) | undefined
  readonly selectionnee?: boolean | undefined
  readonly className?: string | undefined
  readonly children: ReactNode
}

export function Tr({ onClick, selectionnee = false, className, children }: TrProps) {
  const cliquable = onClick !== undefined
  return (
    <tr
      className={cn(
        selectionnee ? 'bg-rideau-50' : 'bg-white',
        cliquable && 'cursor-pointer hover:bg-slate-50',
        className,
      )}
      onClick={onClick}
      // Une ligne cliquable reste atteignable au clavier.
      tabIndex={cliquable ? 0 : undefined}
      onKeyDown={
        cliquable
          ? (evenement) => {
              if (evenement.key === 'Enter' || evenement.key === ' ') {
                evenement.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      {children}
    </tr>
  )
}

export interface CelluleProps {
  readonly aligne?: Alignement | undefined
  readonly className?: string | undefined
  readonly colSpan?: number | undefined
  readonly children?: ReactNode | undefined
}

export interface ThProps extends CelluleProps {
  /**
   * Colonne triable : `'aucun'` quand le tableau est trié sur une autre
   * colonne. Alimente `aria-sort`, que les lecteurs d’écran annoncent.
   */
  readonly tri?: SensTri | 'aucun' | undefined
}

export function Th({ aligne = 'gauche', className, colSpan, tri, children }: ThProps) {
  return (
    <th
      scope="col"
      colSpan={colSpan}
      aria-sort={tri === undefined ? undefined : tri === 'aucun' ? 'none' : tri === 'asc' ? 'ascending' : 'descending'}
      className={cn(
        'px-3 py-2.5 text-xs font-semibold tracking-wide text-slate-600 uppercase',
        ALIGNEMENTS[aligne],
        className,
      )}
    >
      {children}
    </th>
  )
}

export function Td({ aligne = 'gauche', className, colSpan, children }: CelluleProps) {
  return (
    <td colSpan={colSpan} className={cn('px-3 py-2.5 text-slate-800', ALIGNEMENTS[aligne], className)}>
      {children}
    </td>
  )
}

/* -------------------------------------------------------------------------- */
/* Tri de colonne                                                             */
/* -------------------------------------------------------------------------- */

export type SensTri = 'asc' | 'desc'

/** Colonne triée et sens du tri — l’état que porte l’écran. */
export interface EtatTri<TCle extends string = string> {
  readonly cle: TCle
  readonly sens: SensTri
}

/**
 * Bascule de tri commune à toutes les listes : recliquer la colonne courante
 * inverse le sens, cliquer une autre colonne la trie dans son sens naturel
 * (`sensInitial` — `'desc'` pour une date ou un montant, où l’on veut d’abord
 * voir « le plus récent » et « le plus gros »).
 */
export function basculerTri<TCle extends string>(
  etat: EtatTri<TCle>,
  cle: TCle,
  sensInitial: SensTri = 'asc',
): EtatTri<TCle> {
  if (etat.cle !== cle) return { cle, sens: sensInitial }
  return { cle, sens: etat.sens === 'asc' ? 'desc' : 'asc' }
}

export interface ColonneTriableProps<TCle extends string> {
  /** Clé de la colonne portée par ce bouton. */
  readonly cle: TCle
  readonly etat: EtatTri<TCle>
  /** Reçoit la colonne cliquée ; l’écran applique `basculerTri`. */
  readonly onTrier: (cle: TCle) => void
  readonly aligne?: Alignement | undefined
  /** Libellé lu par les lecteurs d’écran si `children` n’est pas du texte. */
  readonly libelle?: string | undefined
  readonly children: ReactNode
}

/**
 * En-tête de colonne cliquable — **le seul** patron de tri de l’application.
 * À placer dans un `<Th>` fait main, ou dans le champ `entete` d’une
 * `Colonne` de `<DataTable>` (qui fournit déjà le `<Th>`).
 */
export function ColonneTriable<TCle extends string>({
  cle,
  etat,
  onTrier,
  aligne = 'gauche',
  libelle,
  children,
}: ColonneTriableProps<TCle>) {
  const actif = etat.cle === cle
  const texte = libelle ?? (typeof children === 'string' ? children : undefined)

  return (
    <button
      type="button"
      onClick={() => { onTrier(cle) }}
      aria-label={
        texte === undefined
          ? undefined
          : actif
            ? `Trier par ${texte} — actuellement ${etat.sens === 'asc' ? 'croissant' : 'décroissant'}`
            : `Trier par ${texte}`
      }
      className={cn(
        // Le `-my-3.5 py-3.5` étend la zone tapable dans le rembourrage du `<th>`
        // sans épaissir la ligne d’en-tête : 44 px au doigt, même allure à l’œil.
        'inline-flex items-center gap-1 font-semibold tracking-wide text-slate-600 uppercase hover:text-slate-900',
        '-my-3.5 py-3.5 sm:my-0 sm:py-0',
        aligne === 'droite' && 'flex-row-reverse',
      )}
    >
      {children}
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={cn('size-3 shrink-0', !actif && 'opacity-30')}
        aria-hidden="true"
      >
        {actif && etat.sens === 'desc' ? (
          <path d="m6 8 4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <path d="m6 12 4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
    </button>
  )
}

/** `<Th>` triable prêt à l’emploi, pour les tableaux écrits à la main. */
export function ThTriable<TCle extends string>({
  aligne = 'gauche',
  className,
  ...props
}: ColonneTriableProps<TCle> & { readonly className?: string | undefined }) {
  return (
    <Th aligne={aligne} className={className} tri={props.etat.cle === props.cle ? props.etat.sens : 'aucun'}>
      <ColonneTriable {...props} aligne={aligne} />
    </Th>
  )
}

/* -------------------------------------------------------------------------- */
/* DataTable                                                                  */
/* -------------------------------------------------------------------------- */

export interface Colonne<T> {
  /** Identifiant technique de la colonne (clé React). */
  readonly cle: string
  readonly entete: ReactNode
  readonly rendu: (ligne: T, index: number) => ReactNode
  readonly aligne?: Alignement | undefined
  /** Renseigné pour une colonne triable : alimente `aria-sort` sur l’en-tête. */
  readonly tri?: SensTri | 'aucun' | undefined
  /** Classe de largeur Tailwind (`w-32`), sinon la colonne s’ajuste. */
  readonly largeur?: string | undefined
  /** Masque la colonne sur petit écran. */
  readonly secondaire?: boolean | undefined
}

export interface DataTableProps<T> {
  readonly donnees: readonly T[]
  readonly colonnes: readonly Colonne<T>[]
  readonly cleLigne: (ligne: T, index: number) => string
  /** Affiché à la place du tableau quand `donnees` est vide. */
  readonly vide?: ReactNode | undefined
  readonly onLigneClic?: ((ligne: T) => void) | undefined
  readonly ligneSelectionnee?: ((ligne: T) => boolean) | undefined
  readonly legende?: ReactNode | undefined
  readonly className?: string | undefined
}

export function DataTable<T>({
  donnees,
  colonnes,
  cleLigne,
  vide,
  onLigneClic,
  ligneSelectionnee,
  legende,
  className,
}: DataTableProps<T>) {
  if (donnees.length === 0 && vide !== undefined) return <>{vide}</>

  return (
    <Table legende={legende} className={className}>
      <Thead>
        <tr>
          {colonnes.map((colonne) => (
            <Th
              key={colonne.cle}
              aligne={colonne.aligne ?? 'gauche'}
              tri={colonne.tri}
              className={cn(colonne.largeur, colonne.secondaire === true && 'hidden lg:table-cell')}
            >
              {colonne.entete}
            </Th>
          ))}
        </tr>
      </Thead>
      <Tbody>
        {donnees.map((ligne, index) => (
          <Tr
            key={cleLigne(ligne, index)}
            onClick={onLigneClic === undefined ? undefined : () => { onLigneClic(ligne) }}
            selectionnee={ligneSelectionnee?.(ligne) ?? false}
          >
            {colonnes.map((colonne) => (
              <Td
                key={colonne.cle}
                aligne={colonne.aligne ?? 'gauche'}
                // La largeur suit la colonne jusque dans le corps : posée sur le
                // seul en-tête, une largeur minimale ne survit pas à une cellule
                // de texte que le navigateur juge compressible.
                className={cn(colonne.largeur, colonne.secondaire === true && 'hidden lg:table-cell')}
              >
                {colonne.rendu(ligne, index)}
              </Td>
            ))}
          </Tr>
        ))}
      </Tbody>
    </Table>
  )
}
